import { SelectCategorieDepense } from '../components/shared/SelectCategorieDepense';
import { useEffect, useState } from 'react';
import { Camera, Sparkles, Check, Trash2, Upload } from '../lib/icons';
import { toast } from 'sonner';
import {
  analyserFacture,
  fetchBrouillons,
  ajouterBrouillon,
  supprimerBrouillon,
  creerOperationCoursier,
  fetchOperationsCoursier,
  fetchTiers,
  fetchPersonnel,
  fetchFinances,
  nomTiers,
  TYPES_OPERATION,
  SENS_TIERS,
  MOTIFS_PAIE,
} from '../lib/dataProvider';
import { compresserFichier } from '../lib/image';
import { deposerDocument } from '../lib/docs';
import { useAuth } from '../lib/auth';
import { caisseImposee } from '../lib/permissions';
import { ContactCombobox } from '../components/shared/ContactCombobox';
import { fmt, PageTitle } from '../components/shared/badges';

const inputCls = 'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none focus:border-slate-900 focus:bg-white';
const label = 'text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';

// Écran mobile : photographier une facture fournisseur (→ brouillons OCR partagés) ou
// un reçu de dépense du coursier (→ opération « En attente » à valider par la direction).
export default function ScanMobile() {
  const { user } = useAuth();
  const [mode, setMode] = useState('facture');

  return (
    <div data-testid="page-scan-mobile" className="mx-auto max-w-xl space-y-4">
      <PageTitle titre="Scan depuis mobile" sous="Photographiez le document : l'IA le lit et l'envoie au bureau." />

      <div className="grid grid-cols-2 gap-px border border-slate-200 bg-slate-200">
        {[
          { id: 'facture', label: 'Facture fournisseur', hint: 'Brouillon à valider' },
          { id: 'recu', label: 'Reçu de dépense', hint: 'Soumis à validation' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`scan-mode-${t.id}`}
            onClick={() => setMode(t.id)}
            className={`px-3 py-3 text-left transition-colors duration-150 ${mode === t.id ? 'bg-slate-900 text-white' : 'bg-white'}`}
          >
            <p className="text-sm font-bold">{t.label}</p>
            <p className={`text-xs ${mode === t.id ? 'text-slate-300' : 'text-slate-500'}`}>{t.hint}</p>
          </button>
        ))}
      </div>

      {mode === 'facture' ? <ScanFacture user={user} /> : <ScanRecu user={user} />}
    </div>
  );
}

const Zone = ({ encours, titre, aide, testid, onFichiers, accepteGalerie = 'image/*,.pdf' }) => (
  <>
    <label
      data-testid={testid}
      className={`flex cursor-pointer flex-col items-center justify-center gap-3 border-4 border-dashed px-4 py-12 text-center transition-colors duration-150 ${
        encours ? 'border-violet-500 bg-violet-50' : 'border-slate-900 bg-slate-50 active:bg-slate-100'
      }`}
    >
      {encours ? <Sparkles size={44} className="animate-pulse text-violet-700" /> : <Camera size={44} className="text-slate-900" />}
      <span className="font-heading text-lg font-black uppercase tracking-[0.04em] text-slate-900">{encours ?? titre}</span>
      <span className="text-xs text-slate-500">{aide}</span>
      <input
        data-testid={`${testid}-input`}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        disabled={Boolean(encours)}
        onChange={(e) => onFichiers(e.target.files)}
      />
    </label>
    <label data-testid={`${testid}-galerie`} className="flex h-12 cursor-pointer items-center justify-center gap-2 border border-slate-300 bg-white text-sm font-bold text-slate-700 active:bg-slate-100">
      <Upload size={16} /> Choisir un fichier déjà enregistré
      <input
        data-testid={`${testid}-galerie-input`}
        type="file"
        accept={accepteGalerie}
        multiple
        className="hidden"
        disabled={Boolean(encours)}
        onChange={(e) => onFichiers(e.target.files)}
      />
    </label>
  </>
);

// ───────── Mode 1 : facture fournisseur → file de brouillons OCR ─────────
const ScanFacture = ({ user }) => {
  const [brouillons, setBrouillons] = useState(fetchBrouillons());
  const [encours, setEncours] = useState(null);

  const traiter = async (files) => {
    const liste = Array.from(files ?? []);
    if (!liste.length) return;
    for (const [i, brutFichier] of liste.entries()) {
      setEncours(`${i + 1}/${liste.length} — compression…`);
      let fichier = brutFichier;
      let apercu = null;
      try {
        if (brutFichier.type.startsWith('image/')) {
          const r = await compresserFichier(brutFichier);
          fichier = r.fichier;
          apercu = r.dataUrl;
        }
      } catch (err) {
        toast.error(err.message);
        continue;
      }
      setEncours(`${i + 1}/${liste.length} — lecture IA…`);
      const { error, facture } = await analyserFacture(fichier);
      if (error) {
        toast.error(error);
        continue;
      }
      const depot = await deposerDocument(fichier, {
        source: 'scan_mobile',
        categorie: 'Facture fournisseur',
        deposePar: user?.name,
        indexer: false,
      });
      if (depot.doublon) {
        toast.warning(`Document déjà traité (${depot.document.nom}) — aucun brouillon créé`, { duration: 9000 });
        continue;
      }
      ajouterBrouillon({
        id: `BR-${Date.now()}-${i}`,
        fichier: fichier.name,
        apercu: depot.url ?? apercu,
        documentId: depot.document?.id ?? null,
        mime: fichier.type,
        extrait: facture,
        source: 'mobile',
        par: user?.name || user?.email || 'mobile',
        le: new Date().toISOString().slice(0, 16).replace('T', ' '),
      });
      setBrouillons(fetchBrouillons());
      toast.success(`${facture?.fournisseur?.nom || 'Facture'} envoyée dans les brouillons`);
    }
    setEncours(null);
  };

  const mobiles = brouillons.filter((b) => b.source === 'mobile');

  return (
    <div className="space-y-4">
      <Zone
        testid="scan-camera"
        encours={encours}
        titre="Prendre la facture en photo"
        aide="L'IA lit fournisseur, date, n° de facture, HT, TVA, FODEC et timbre fiscal."
        onFichiers={traiter}
      />

      <section className="border border-slate-200 bg-white">
        <p className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
          Factures envoyées depuis ce téléphone ({mobiles.length})
        </p>
        {mobiles.length === 0 ? (
          <p data-testid="scan-vide" className="px-3 py-6 text-center text-xs text-slate-500">
            Aucun scan pour l'instant. Les factures envoyées apparaissent ici puis dans « Dépenses & Frais Généraux » au bureau.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {mobiles.map((b) => (
              <div key={b.id} data-testid={`scan-item-${b.id}`} className="flex items-center justify-between gap-2 px-3 py-2">
                <p className="font-data text-xs text-slate-700">
                  <span className="font-bold text-slate-900">{b.extrait?.fournisseur?.nom || 'Fournisseur illisible'}</span>
                  <br />
                  {b.extrait?.numeroFacture || 'n° ?'} · {b.extrait?.date || 'date ?'} ·{' '}
                  <span className="font-bold">{fmt(b.extrait?.montantTTC ?? 0, 3)} TND</span>
                  <span className="ml-1 inline-flex items-center gap-1 text-emerald-700">
                    <Check size={11} /> en attente de validation
                  </span>
                </p>
                <button
                  type="button"
                  data-testid={`scan-supprimer-${b.id}`}
                  onClick={() => { supprimerBrouillon(b.id); setBrouillons(fetchBrouillons()); }}
                  className="border border-slate-300 p-2 text-slate-500 active:bg-red-600 active:text-white"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

// ───────── Mode 2 : reçu de dépense du coursier → opération en attente ─────────
const ScanRecu = ({ user }) => {
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const caisseFixe = caisseImposee(user?.role);
  const [encours, setEncours] = useState(null);
  const [ops, setOps] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [f, setF] = useState({
    date: aujourdhui,
    typeOperation: 'frais',
    montant: '',
    categorie: '',
    libelle: '',
    tiersId: '',
    sensTiers: SENS_TIERS[0],
    employeId: '',
    motifPaie: MOTIFS_PAIE[0],
    justificatif: null,
    compteId: caisseFixe ?? '',
  });

  const load = async () => {
    setOps(await fetchOperationsCoursier());
    setTiers(await fetchTiers());
    setPersonnel(await fetchPersonnel());
    const soldes = (await fetchFinances()).soldes;
    setComptes(caisseFixe ? soldes.filter((c) => c.id === caisseFixe) : soldes);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Photo du reçu : compression puis pré-remplissage IA du montant et du motif.
  const photographier = async (files) => {
    const file = Array.from(files ?? [])[0];
    if (!file) return;
    setEncours('Compression de la photo…');
    let fichier = file;
    let apercu = null;
    try {
      const r = await compresserFichier(file);
      fichier = r.fichier;
      apercu = r.dataUrl;
    } catch (err) {
      setEncours(null);
      return toast.error(err.message);
    }
    setF((p) => ({ ...p, justificatif: apercu }));
    setEncours('Archivage au coffre-fort…');
    const depot = await deposerDocument(fichier, { source: 'recu_coursier', deposePar: user?.name, indexer: false });
    if (depot.url) setF((p) => ({ ...p, justificatif: depot.url, documentId: depot.document?.id ?? null }));
    if (depot.doublon) toast.warning(`Ce reçu est déjà archivé (${depot.document.nom}) — document existant réutilisé`);
    setEncours('Lecture IA du reçu…');
    const { error, facture } = await analyserFacture(fichier);
    setEncours(null);
    if (error) return toast.warning(`Photo attachée — lecture IA impossible (${error}) : saisissez le montant.`);
    setF((p) => ({
      ...p,
      montant: facture?.montantTTC ? String(facture.montantTTC) : p.montant,
      date: facture?.date || p.date,
      libelle: p.libelle || facture?.objet || facture?.fournisseur?.nom || '',
    }));
    toast.success('Reçu lu : vérifiez le montant et le motif');
  };

  const envoyer = async (e) => {
    e.preventDefault();
    if (!f.justificatif) return toast.error('Photographiez le reçu avant de l’envoyer.');
    const res = await creerOperationCoursier({ ...f, saisiPar: user?.name });
    if (res.error) return toast.error(res.error);
    const mt = fmt(Number(f.montant), 3);
    if (res.statut === 'validee') {
      toast.success(`Dépense de ${mt} TND validée et imputée`);
    } else if (res.statut === 'validation_echouee') {
      toast.error(
        res.indetermine
          ? `Dépense de ${mt} TND ENREGISTRÉE sous ${res.row?.id} — validation au RÉSULTAT INDÉTERMINÉ : ` +
            `${res.detail} Ne la ressaisissez pas : faites vérifier la base.`
          : `Dépense de ${mt} TND ENREGISTRÉE sous ${res.row?.id}, mais NON validée : ${res.detail} ` +
            'Ne la ressaisissez pas : la direction la validera depuis l’écran Validations.',
        { duration: 14000 }
      );
    } else {
      toast.success(`Dépense de ${mt} TND enregistrée, en attente de validation`);
    }
    setF({ ...f, montant: '', libelle: '', categorie: '', tiersId: '', employeId: '', justificatif: null });
    load();
  };

  const mesOps = ops.filter((o) => o.statut === 'En attente').slice(0, 8);

  return (
    <form onSubmit={envoyer} data-testid="scan-recu-form" className="space-y-4">
      <Zone
        testid="scan-recu"
        encours={encours}
        titre="Photographier le reçu"
        aide="L'IA pré-remplit le montant et le motif ; la direction valide ensuite le décaissement."
        accepteGalerie="image/*"
        onFichiers={photographier}
      />

      {f.justificatif && (
        <div className="flex items-center gap-3 border border-emerald-300 bg-emerald-50 px-3 py-2">
          <img data-testid="scan-recu-apercu" src={f.justificatif} alt="Reçu" className="h-16 w-16 border border-emerald-200 object-cover" />
          <p className="text-xs font-bold text-emerald-800">Photo du reçu attachée</p>
          <button type="button" data-testid="scan-recu-retirer" onClick={() => setF({ ...f, justificatif: null })} className="ml-auto border border-slate-300 bg-white p-2 text-slate-500">
            <Trash2 size={14} />
          </button>
        </div>
      )}

      <div className="grid grid-cols-3 gap-px border border-slate-200 bg-slate-200">
        {TYPES_OPERATION.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`scan-type-${t.id}`}
            onClick={() => setF((p) => ({ ...p, typeOperation: t.id }))}
            className={`px-2 py-2 text-center text-xs font-bold transition-colors duration-150 ${f.typeOperation === t.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 border border-slate-200 bg-white p-3 sm:grid-cols-2">
        <div>
          <label className={label}>Date</label>
          <input data-testid="scan-recu-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={label}>Montant (TND) *</label>
          <input data-testid="scan-recu-montant" type="number" step="0.001" min="0" value={f.montant} onChange={(e) => setF({ ...f, montant: e.target.value })} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label className={label}>Caisse / source de trésorerie *</label>
          <select data-testid="scan-recu-compte" value={f.compteId} onChange={(e) => setF({ ...f, compteId: e.target.value })} disabled={Boolean(caisseFixe)} className={`${inputCls} disabled:bg-slate-100`}>
            {!caisseFixe && <option value="">— D’où sort l’argent ? —</option>}
            {comptes.map((c) => <option key={c.id} value={c.id}>{`${c.nom} — ${fmt(c.solde, 3)} TND`}</option>)}
          </select>
        </div>

        {f.typeOperation === 'frais' && (
          <div className="sm:col-span-2">
            <label className={label}>Catégorie</label>
            <SelectCategorieDepense usage="depenses" data-testid="scan-recu-categorie" value={f.categorie} onChange={(e) => setF({ ...f, categorie: e.target.value })} className={inputCls}>
                <option value="">— Choisir —</option>
              </SelectCategorieDepense>
          </div>
        )}

        {f.typeOperation === 'tiers' && (
          <>
            <div className="sm:col-span-2">
              <label className={label}>Tiers (client / fournisseur) *</label>
              <ContactCombobox contacts={tiers} value={f.tiersId} onChange={(id) => setF({ ...f, tiersId: id })} testid="scan-recu-tiers" />
            </div>
            <div className="sm:col-span-2">
              <label className={label}>Nature</label>
              <select data-testid="scan-recu-sens" value={f.sensTiers} onChange={(e) => setF({ ...f, sensTiers: e.target.value })} className={inputCls}>
                {SENS_TIERS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </>
        )}

        {f.typeOperation === 'personnel' && (
          <>
            <div className="sm:col-span-2">
              <label className={label}>Employé *</label>
              <ContactCombobox contacts={personnel} value={f.employeId} onChange={(id) => setF({ ...f, employeId: id })} testid="scan-recu-employe" />
            </div>
            <div className="sm:col-span-2">
              <label className={label}>Motif</label>
              <select data-testid="scan-recu-motif" value={f.motifPaie} onChange={(e) => setF({ ...f, motifPaie: e.target.value })} className={inputCls}>
                {MOTIFS_PAIE.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </>
        )}

        <div className="sm:col-span-2">
          <label className={label}>Motif / libellé *</label>
          <input data-testid="scan-recu-libelle" value={f.libelle} onChange={(e) => setF({ ...f, libelle: e.target.value })} className={inputCls} placeholder="ex. gasoil camion, pièce pompe…" />
        </div>
      </div>

      <button type="submit" data-testid="scan-recu-envoyer" className="h-12 w-full bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white active:bg-slate-700">
        Envoyer pour validation
      </button>

      <section className="border border-slate-200 bg-white">
        <p className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
          Mes dépenses en attente de validation ({mesOps.length})
        </p>
        {mesOps.length === 0 ? (
          <p data-testid="scan-recu-vide" className="px-3 py-6 text-center text-xs text-slate-500">Aucune dépense en attente.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {mesOps.map((o) => (
              <p key={o.id} data-testid={`scan-recu-item-${o.id}`} className="px-3 py-2 font-data text-xs text-slate-700">
                {o.date} · <span className="font-bold">{fmt(o.montant, 3)} TND</span> · {o.libelle}
                {o.tiersId ? ` · ${nomTiers(o.tiersId)}` : o.employeId ? ` · ${nomTiers(o.employeId)}` : o.categorie ? ` · ${o.categorie}` : ''}
              </p>
            ))}
          </div>
        )}
      </section>
    </form>
  );
};
